import { useQueryClient } from '@tanstack/react-query'
import { EyeIcon, KeyRoundIcon } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'

import { useNow, useViewAs } from './hooks'
import { BANNER_END_REASON, dropConsoleCache, finishBreakglass, finishViewAs } from './session'
import { clearViewAs, formatCountdown, isLive, secondsLeft, setBreakglass } from './view-as'

/**
 * The fixed bars shown on every console page while a view-as session is live: the red read-only bar
 * (S13) and, with a break-glass grant, an amber one with its countdown and "End now" (S6). Both sit
 * at the bottom of the viewport, so they never cover the page header or the mobile top bar.
 */
export function ViewAsBanner() {
  const session = useViewAs()
  const client = useQueryClient()
  const now = useNow(session === null ? 0 : 1000)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const sessionOver = session !== null && !isLive(session.expiresAt, now)
  const grant = session?.breakglass ?? null
  const grantOver = grant !== null && !isLive(grant.expiresAt, now)

  // A session or grant that ran out is dropped locally; the server stopped honouring it already.
  useEffect(() => {
    if (sessionOver) {
      clearViewAs()
      dropConsoleCache(client)
    } else if (grantOver) {
      setBreakglass(null)
      dropConsoleCache(client)
    }
  }, [sessionOver, grantOver, client])

  if (session === null || sessionOver) return null

  async function run(action: () => Promise<void>) {
    setBusy(true)
    setError(undefined)
    try {
      await action()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not end it.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] flex flex-col" data-testid="view-as-banners">
      {grant === null || grantOver ? null : (
        <div
          role="status"
          data-testid="breakglass-banner"
          className="flex flex-wrap items-center justify-between gap-3 bg-amber-500 px-4 py-2 text-sm font-medium text-black"
        >
          <span className="flex items-center gap-2">
            <KeyRoundIcon className="size-4" aria-hidden="true" />
            Break-glass: message bodies of one zone are readable.{' '}
            <span className="tabular-nums" data-testid="breakglass-countdown">
              {formatCountdown(secondsLeft(grant.expiresAt, now))}
            </span>{' '}
            left
          </span>
          <Button
            size="sm"
            variant="outline"
            className="border-black/40 bg-transparent text-black hover:bg-black/10"
            disabled={busy}
            onClick={() => {
              void run(() => finishBreakglass(client, BANNER_END_REASON))
            }}
          >
            End now
          </Button>
        </div>
      )}
      <div
        role="status"
        data-testid="view-as-banner"
        className="flex flex-wrap items-center justify-between gap-3 bg-red-700 px-4 py-2 text-sm font-medium text-white"
      >
        <span className="flex items-center gap-2">
          <EyeIcon className="size-4" aria-hidden="true" />
          Viewing <strong>{session.accountName}</strong> as staff, read-only ·{' '}
          <span className="tabular-nums">{formatCountdown(secondsLeft(session.expiresAt, now))}</span> left
          {error === undefined ? null : <span className="font-normal"> — {error}</span>}
        </span>
        <Button
          size="sm"
          variant="outline"
          className="border-white/60 bg-transparent text-white hover:bg-white/15"
          disabled={busy}
          onClick={() => {
            void run(() => finishViewAs(client, BANNER_END_REASON))
          }}
        >
          End
        </Button>
      </div>
    </div>
  )
}
